import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import NodeActivityEditor from '../../NodeActivityEditor';
import IconButton from '../../IconButton';
import ProgressBar from '../../ProgressBar';
import LegacyMedia from '../../LegacyMedia';
import CheckIcon from '../../../../public/assets/images/theme-2/icons/handin-hover.svg';
import { formatBody } from '../../../utils';
import {
  PreviewSection,
  Questions,
  Question,
  SubmitPanel,
  Alternatives,
  AlternativeWrapper,
  Alternative,
  AlternativeOption,
  InfoPanel,
  Info,
  PreviewBottomPanel,
  ProgressPanel,
  QuizInfo,
} from '../styles';
import ImmersiveReaderButton from '../../ImmersiveReaderButton';

const QuizContentPreview = ({ type, previewText, data }) => {
  const [markedAnswers, setMarkedAnswers] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [progress, setProgress] = useState(0);
  const [corrected, setCorrected] = useState(false);
  const [correctAnswers, setCorrectAnswers] = useState(0);

  const questionCount = questions?.length || 0;

  useEffect(() => {
    const newQuestions = data.QuizQuestions || [];
    setQuestions(newQuestions);
    setMarkedAnswers(new Array(newQuestions.length));
  }, []);

  useEffect(() => {
    const count = markedAnswers.filter((answer) => !!answer).length;
    setProgress(count);
  }, [markedAnswers]);

  const scrollToQuestion = (index) => {
    const question = document.getElementById(`question_${index}`);
    const contentPanel = document.getElementsByClassName(`content-panel`)[0];
    if (question && contentPanel) {
      contentPanel.scrollTo(0, question.offsetTop - 80);
    }
  };

  const getPreviewForAlternative = (alternative, questionIndex, isQuestionCorrect) => {
    const { id, body, correct } = alternative;

    const markAnswer = (event) => {
      if (!corrected) {
        event.target.blur();
        const newMarkedAnswers = [...markedAnswers];
        newMarkedAnswers[questionIndex] = id;
        setMarkedAnswers(newMarkedAnswers);
        scrollToQuestion(questionIndex + 1);
      }
    };

    let className = '';
    if (corrected && !isQuestionCorrect) {
      if (correct) {
        className = 'correct';
      } else if (markedAnswers[questionIndex] === id) {
        className = 'error';
      }
    }

    const alternativeBody = (
      <Alternative className="content-section" dangerouslySetInnerHTML={{ __html: formatBody(body) }} />
    );

    return (
      <>
        {alternative.media_preview ? (
          <div>
            {alternativeBody}
            <LegacyMedia item={alternative} />
          </div>
        ) : (
          alternativeBody
        )}
        <AlternativeOption
          onClick={markAnswer}
          disabled={corrected}
          selected={markedAnswers[questionIndex] === id}
          title={i18n.__('quiz_mark_correct_answer')}
          className={className}
        />
      </>
    );
  };

  const getPreviewForQuestion = (question, questionIndex) => {
    const correctAlternative = question.QuizAlternatives.find((alternative) => alternative.correct);
    const isQuestionCorrect = markedAnswers[questionIndex] === correctAlternative?.id;

    const editorPanel = (
      <Alternatives>
        {question.QuizAlternatives.map((alternative) => (
          <AlternativeWrapper key={alternative.id}>
            {getPreviewForAlternative(alternative, questionIndex, isQuestionCorrect)}
          </AlternativeWrapper>
        ))}
      </Alternatives>
    );

    const headerRightPanel = <ImmersiveReaderButton />;

    return (
      <NodeActivityEditor
        type={type}
        index={questionIndex}
        legacyContent={question}
        headerText={formatBody(question.body)}
        headerRightPanel={headerRightPanel}
        editorPanel={editorPanel}
        className={corrected && !isQuestionCorrect ? 'error' : ''}
      />
    );
  };

  const mappedQuestions = questions.map((question, index) => (
    <Question id={`question_${index}`} key={question.id}>
      {getPreviewForQuestion(question, index)}
    </Question>
  ));

  const correctQuiz = () => {
    let correct = 0;
    questions.forEach((question, index) => {
      const correctAnswer = question.QuizAlternatives.find((alternative) => alternative.correct);
      if (correctAnswer?.id === markedAnswers[index]) {
        correct++;
      }
    });
    setCorrectAnswers(correct);
    setCorrected(true);
  };

  const resetQuiz = () => {
    if (data.redoable) {
      setMarkedAnswers(new Array(questionCount));
      setCorrectAnswers(0);
      setCorrected(false);
    }
  };

  return (
    <>
      <PreviewSection>
        <Questions>{mappedQuestions}</Questions>
        <QuizInfo>{i18n.__(data.redoable ? 'correlation_redoable_desc' : 'correlation_not_redoable_desc')}</QuizInfo>
      </PreviewSection>
      <PreviewBottomPanel>{previewText}</PreviewBottomPanel>
      <SubmitPanel className={'ir-hide'}>
        {progress ? (
          <ProgressPanel>
            {data.redoable || !corrected ? (
              <IconButton
                icon={CheckIcon}
                disabled={progress !== questionCount}
                onClick={corrected ? resetQuiz : correctQuiz}
              >
                {i18n.__(corrected ? 'app_reset' : 'app_correct')}
              </IconButton>
            ) : (
              <div> </div>
            )}
            <Info>
              {corrected
                ? i18n.__('quiz_correcting_result', correctAnswers, questionCount)
                : i18n.__('quiz_ongoing_question_count', progress, questionCount)}
              <ProgressBar
                key={progress}
                progress={Math.round(((corrected ? correctAnswers : progress) / questionCount) * 100)}
              />
            </Info>
          </ProgressPanel>
        ) : (
          <InfoPanel>
            <Info>{i18n.__('quiz_question_count', questionCount)}</Info>
          </InfoPanel>
        )}
      </SubmitPanel>
    </>
  );
};

export default QuizContentPreview;

QuizContentPreview.propTypes = {
  type: PropTypes.string.isRequired,
  previewText: PropTypes.string.isRequired,
  data: PropTypes.object.isRequired,
};
